import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EditorButton,
  Icon,
  useElementFromRef,
} from "@qw/ui";
import { useCurrentEditor } from "@tiptap/react";
import { type FC, type RefObject, useCallback, useState } from "react";
import { getTemplateConditionalParams, type TemplateParamDefinition } from "./playbook-template-params";
import { TEMPLATE_CONDITIONAL_NODE_NAME } from "./playbook-workflow-extensions/editor-extension-template-conditional";
import type { TemplateSlotDeclaredParam } from "./playbook-workflow-extensions/editor-extension-template-slot";

interface PlaybookWorkflowTemplateControlsProps {
  containerRef?: RefObject<HTMLDivElement | null>;
  getDeclaredParams?: () => TemplateSlotDeclaredParam[];
}

export const getTemplateConditionalMenuItems = (
  declaredParams: TemplateSlotDeclaredParam[]
): TemplateParamDefinition[] => {
  const params = declaredParams.filter((param): param is TemplateParamDefinition => {
    return (
      (param.type === "string" || param.type === "text" || param.type === "boolean" || param.type === "enum") &&
      typeof param.required === "boolean"
    );
  });

  return getTemplateConditionalParams(params);
};

export const PlaybookWorkflowTemplateControls: FC<PlaybookWorkflowTemplateControlsProps> = ({
  containerRef,
  getDeclaredParams,
}) => {
  const container = useElementFromRef(containerRef);
  const [isOpen, setIsOpen] = useState(false);
  const { editor } = useCurrentEditor();

  const booleanParams = getTemplateConditionalMenuItems(getDeclaredParams?.() ?? []);
  const activeParam = booleanParams.find((param) =>
    editor?.isActive(TEMPLATE_CONDITIONAL_NODE_NAME, { param: param.name })
  );
  // Keep the menu reachable whenever the selection is inside a conditional wrapper —
  // even with no declared boolean params — so "Remove conditional" is always
  // available. Otherwise deleting/retyping the last boolean field would strand an
  // invalid wrapper with no way to remove it.
  const isInsideConditional = editor?.isActive(TEMPLATE_CONDITIONAL_NODE_NAME) ?? false;

  const handleSetConditional = useCallback(
    (param: string) => {
      if (!editor) {
        return;
      }

      // Retarget the conditional the cursor is already in rather than wrapping
      // again: wrapping would nest a second data-tpl-if, so the content would
      // depend on both flags (or be double-wrapped in the same one).
      if (editor.isActive(TEMPLATE_CONDITIONAL_NODE_NAME)) {
        editor.chain().focus().updateAttributes(TEMPLATE_CONDITIONAL_NODE_NAME, { param }).run();
        return;
      }

      editor.chain().focus().setTemplateConditional(param).run();
    },
    [editor]
  );

  const handleUnsetConditional = useCallback(() => {
    editor?.chain().focus().unsetTemplateConditional().run();
  }, [editor]);

  const handleCloseAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
  }, []);

  if (!editor) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <EditorButton
          icon="rule"
          tooltip="Conditional block"
          disabled={booleanParams.length === 0 && !isInsideConditional}
          className="pr-2"
        >
          <Icon icon="keyboard_arrow_down" className="size-4" />
        </EditorButton>
      </DropdownMenuTrigger>

      {isOpen && (
        <DropdownMenuContent align="start" onCloseAutoFocus={handleCloseAutoFocus} container={container}>
          {booleanParams.map((param) => (
            <DropdownMenuCheckboxItem
              key={param.name}
              checked={activeParam?.name === param.name}
              indicatorPosition="end"
              onSelect={() => handleSetConditional(param.name)}
            >
              <Icon icon="rule" className="text-base" />
              <span className="text-sm font-mono">{param.name}</span>
            </DropdownMenuCheckboxItem>
          ))}
          {booleanParams.length > 0 && <DropdownMenuSeparator />}
          <DropdownMenuItem onSelect={handleUnsetConditional}>
            <Icon icon="remove_selection" className="text-base" />
            <span className="text-sm">Remove conditional</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  );
};
